import { PriceRow, Product, Quarry } from '../types/catalog'
import { prices } from '../data/prices'
import { products } from '../data/products'
import { quarries } from '../data/quarries'

export interface CatalogEntry {
  product: Product
  quarry: Quarry
  price: PriceRow
  unit: string
}

const productById = new Map(products.map((product) => [product.id, product]))
const quarryById = new Map(quarries.map((quarry) => [quarry.id, quarry]))

/** Every published price joined to its product and plant. */
const entries: CatalogEntry[] = prices.flatMap((price) => {
  const product = productById.get(price.productId)
  const quarry = quarryById.get(price.quarryId)
  if (!product || !quarry) return []
  return [{ product, quarry, price, unit: price.unit ?? product.unit }]
})

export interface MaterialGroup {
  category: string
  items: { product: Product; entries: CatalogEntry[]; low: number; high: number }[]
}

/** Materials that at least one plant carries, grouped by category. */
export function materialDirectory(): MaterialGroup[] {
  const byProduct = new Map<string, CatalogEntry[]>()
  entries.forEach((entry) => {
    const list = byProduct.get(entry.product.id) ?? []
    list.push(entry)
    byProduct.set(entry.product.id, list)
  })

  const byCategory = new Map<string, MaterialGroup['items']>()
  products.forEach((product) => {
    const list = byProduct.get(product.id)
    if (!list) return
    const sorted = [...list].sort((a, b) => a.price.price - b.price.price)
    const values = sorted.map((entry) => entry.price.price)
    const items = byCategory.get(product.category) ?? []
    items.push({
      product,
      entries: sorted,
      low: Math.min(...values),
      high: Math.max(...values),
    })
    byCategory.set(product.category, items)
  })

  return [...byCategory.entries()]
    .map(([category, items]) => ({
      category,
      items: items.sort((a, b) => a.product.name.localeCompare(b.product.name)),
    }))
    .sort((a, b) => a.category.localeCompare(b.category))
}

export interface SupplierItem {
  quarry: Quarry
  entries: CatalogEntry[]
}

/** Plants with at least one published price, alphabetically. */
export function supplierDirectory(): SupplierItem[] {
  const byQuarry = new Map<string, CatalogEntry[]>()
  entries.forEach((entry) => {
    const list = byQuarry.get(entry.quarry.id) ?? []
    list.push(entry)
    byQuarry.set(entry.quarry.id, list)
  })

  return quarries
    .filter((quarry) => byQuarry.has(quarry.id))
    .map((quarry) => ({
      quarry,
      entries: [...(byQuarry.get(quarry.id) ?? [])].sort((a, b) =>
        a.product.name.localeCompare(b.product.name),
      ),
    }))
    .sort((a, b) => a.quarry.name.localeCompare(b.quarry.name))
}

/** Case-insensitive match against any of the given strings. */
export function matchesFilter(filter: string, ...fields: string[]): boolean {
  const needle = filter.trim().toLowerCase()
  if (!needle) return true
  return fields.some((field) => field.toLowerCase().includes(needle))
}
